import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore'

import { getFirestoreInstance } from '@/lib/firestore/firebase-singleton'

export type UserAioReport = {
  id: string
  topic: string
  website_url?: string
  date_created?: unknown
  userId?: string
  full_report_available: boolean
  public?: boolean
}

const mapSnapshotToReports = (
  snapshot: Awaited<ReturnType<typeof getDocs>>
): UserAioReport[] =>
  snapshot.docs.map((reportDoc) => {
    const data = reportDoc.data() as Omit<UserAioReport, 'id'>
    return {
      id: reportDoc.id,
      topic: typeof data.topic === 'string' ? data.topic : '',
      website_url: data.website_url,
      date_created: data.date_created,
      userId: data.userId,
      full_report_available: Boolean(data.full_report_available),
      public: data.public,
    }
  })

export const fetchUserAioReports = async (
  uid: string,
  pageSize = 20
): Promise<{ owned: UserAioReport[]; shared: UserAioReport[] }> => {
  const db = getFirestoreInstance()
  const reportsCollection = collection(db, 'aio_analysis_report')

  const [ownedSnap, sharedSnap] = await Promise.all([
    getDocs(
      query(
        reportsCollection,
        where('userId', '==', uid),
        orderBy('date_created', 'desc'),
        limit(pageSize)
      )
    ),
    getDocs(
      query(
        reportsCollection,
        where('access_member_uids', 'array-contains', uid),
        orderBy('date_created', 'desc'),
        limit(pageSize)
      )
    ),
  ])

  const owned = mapSnapshotToReports(ownedSnap)
  const ownedIds = new Set(owned.map((report) => report.id))
  const shared = mapSnapshotToReports(sharedSnap).filter((report) => !ownedIds.has(report.id))

  return { owned, shared }
}

export const getFirstSidebarReportPath = async (uid: string): Promise<string | null> => {
  const { owned, shared } = await fetchUserAioReports(uid, 1)
  const first = owned[0] ?? shared[0]
  if (!first) return null
  return `/ai-overview-analysis/${first.id}`
}

const DEFAULT_POST_LOGIN_PATHS = new Set(['/', '/settings', '/site-management', '/audits'])

export const getPostLoginPath = async (uid: string, nextUrl: string): Promise<string> => {
  const normalizedNext = nextUrl.replace(/\/$/, '') || '/'
  if (!DEFAULT_POST_LOGIN_PATHS.has(normalizedNext)) return nextUrl
  try {
    return (await getFirstSidebarReportPath(uid)) ?? '/'
  } catch {
    return '/'
  }
}
